import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useEditVersionManager from './useEditVersionManager_upgrade';
// import { useBookWorksheet } from './useBookWorksheet';
import { useBookWorksheetCoreZoneBased } from './core/useBookWorksheetCoreZoneBased';
import { useBwVariationVersion } from './useBwVariationVersion';
import {
  selectCharacterZoneChangeCount,
  selectVariationFormChangeCount,
  selectVariationListChangeCount,
  selectVariationVersionsListChangeCount
} from '../slices/bookWorksheetDataSlice';

/**
 * 🚀 ZONE-BASED: Stable wrapper for useEditVersionManager - only updates on Variation Zone form editing + cross-zone events
 * This prevents unnecessary re-renders by creating stable references
 * ENHANCED: Now includes activeVersion data in dependencies for proper form sync
 */
export const useStableEditVersionManager = () => {
  // 🚀 ZONE-BASED: Only watch Variation Zone form editing + cross-zone character selection
  const formChangeCount = useSelector(selectVariationFormChangeCount);
  const editVersionManager = useEditVersionManager();
  const activeVersionUuid = editVersionManager.activeVersion?.uuid;

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[ZONE_STABLE_HOOK] useStableEditVersionManager updated, changeCount:', formChangeCount, 'activeVersion:', {
        uuid: activeVersionUuid
      });
    }

    // Return the manager data - this will only re-compute when Variation Zone form editing changes OR activeVersion changes
    return editVersionManager;
  }, [editVersionManager, formChangeCount, activeVersionUuid]); // 🚀 PERFORMANCE: Depend on formChangeCount and activeVersion for proper sync
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for useBookWorksheet - only updates on Character Zone events (completely isolated)
 * This prevents unnecessary re-renders by creating stable references
 */
export const useStableBookWorksheet = () => {
  // 🚀 ZONE-BASED: ONLY watch Character Zone events for pure isolation
  const characterZoneChangeCount = useSelector(selectCharacterZoneChangeCount);

  // 🚀 ZONE-BASED: Use zone-based core hook that only subscribes to Character Zone data
  const bookWorksheet = useBookWorksheetCoreZoneBased();

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[ZONE_STABLE_HOOK] useStableBookWorksheet updated, zoneCount:', characterZoneChangeCount);
    }

    // Return the worksheet data - this will only re-compute when Character Zone changes
    return bookWorksheet;
  }, [bookWorksheet, characterZoneChangeCount]); // 🚀 ZONE-BASED: ONLY Character Zone events for pure isolation!
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for VariationList - only updates on Variation Zone list changes + cross-zone events
 * This prevents unnecessary re-renders by creating stable references
 * ✅ FIXED: Now includes character selection changes to properly update when character changes
 */
// export const useStableVariationList = () => {
//   // 🚀 ZONE-BASED: Only watch Variation Zone list changes + cross-zone character selection
//   const variationListChangeCount = useSelector(selectVariationListChangeCount);

//   // ✅ FIX: Also watch for character selection changes from shared data
//   const sharedData = useSelector(state => state.user.bookWorksheetData?.sharedData);
//   const characterId = sharedData?.selections?.characterId;

//   const bwVariationVersion = useBwVariationVersion();

//   console.log('[ZONE_STABLE_HOOK] useStableVariationList updated:',
//     'characterId:',
//     characterId, 'variations: ', bwVariationVersion.variations,
//     'count', variationListChangeCount);

//   return useMemo(() => {
//     // if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
//     console.log('[ZONE_STABLE_HOOK] IN MEMO useStableVariationList updated:',
//       'characterId:',
//       characterId, 'variations: ', bwVariationVersion.variations,
//       'count', variationListChangeCount);
//     // Return the variation list data - this will only re-compute when Variation Zone list changes OR character selection changes
//     return bwVariationVersion;

//   }, [variationListChangeCount, characterId]); // ✅ FIXED: Include character selection changes!
// };

// WORKS
export const useStableVariationList = () => {
  const bwVariationVersion = useBwVariationVersion();
  return bwVariationVersion;
};


/**
 * 🚀 ZONE-BASED: Stable wrapper for useBwVariationVersion - only updates on Variation Zone version changes + cross-zone events
 * This prevents unnecessary re-renders by creating stable references
 */
export const useStableBwVariationVersion = () => {
  // 🚀 ZONE-BASED: Only watch Variation Zone version changes + cross-zone character selection
  const versionsListChangeCount = useSelector(selectVariationVersionsListChangeCount);
  const bwVariationVersion = useBwVariationVersion();

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[ZONE_STABLE_HOOK] useStableBwVariationVersion updated, changeCount:', versionsListChangeCount);
    }

    // Return the variation version data - this will only re-compute when Variation Zone version changes
    return bwVariationVersion;
  }, [bwVariationVersion, versionsListChangeCount]); // 🚀 ZONE-BASED: Only Variation Zone version changes + cross-zone events!
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for useVariationManagement - only updates on Variation Zone changes + character selection
 * This prevents unnecessary re-renders by creating stable references
 * ✅ FIXED: Now includes character selection changes to properly update when character changes
 */
export const useStableVariationManagement = () => {
  // 🚀 ZONE-BASED: Watch Variation Zone changes + character selection changes
  const variationListChangeCount = useSelector(selectVariationListChangeCount);
  const variationFormChangeCount = useSelector(selectVariationFormChangeCount);

  // ✅ FIX: Also watch for character selection changes from shared data
  const sharedData = useSelector(state => state.user.bookWorksheetData?.sharedData);
  const characterId = sharedData?.selections?.characterId;

  // Import the hook dynamically to avoid circular dependencies
  const { useVariationManagement } = require('../hooks/core/useZoneHooks');
  const variationManagement = useVariationManagement();

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[ZONE_STABLE_HOOK] useStableVariationManagement updated, listCount:', variationListChangeCount, 'formCount:', variationFormChangeCount, 'characterId:', characterId);
    }

    // Return the variation management data - this will only re-compute when Variation Zone changes OR character selection changes
    return variationManagement;
  }, [variationListChangeCount, variationFormChangeCount, variationManagement, characterId]); // ✅ FIXED: Include character selection changes!
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for useSharedData - only updates on shared data changes
 * This prevents unnecessary re-renders by creating stable references
 * ✅ FIXED: Now properly detects character selection changes by using actual values
 */
export const useStableSharedData = () => {
  // 🚀 ZONE-BASED: Watch shared data changes (lit groups, books, selections)
  const { useSharedData } = require('../hooks/core/useZoneHooks');
  const sharedData = useSharedData();

  // ✅ FIX: Create a change counter using actual values, not just boolean checks
  const sharedDataChangeCount = useMemo(() => {
    return `${sharedData.lastUpdated?.litGroups || 0}-${sharedData.lastUpdated?.books || 0}-${sharedData.selections?.litGroupId || 'null'}-${sharedData.selections?.bookId || 'null'}-${sharedData.selections?.characterId || 'null'}`;
  }, [
    sharedData.lastUpdated?.litGroups,
    sharedData.lastUpdated?.books,
    sharedData.selections?.litGroupId,
    sharedData.selections?.bookId,
    sharedData.selections?.characterId
  ]);

  return useMemo(() => {
    console.log('🎯 [STABLE_SHARED_DATA] useMemo triggered, changeCount:', sharedDataChangeCount);
    console.log('🎯 [STABLE_SHARED_DATA] Current selections:', {
      litGroupId: sharedData.selections?.litGroupId,
      bookId: sharedData.selections?.bookId,
      characterId: sharedData.selections?.characterId
    });

    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[ZONE_STABLE_HOOK] useStableSharedData updated, changeCount:', sharedDataChangeCount);
    }

    // Return the shared data - this will only re-compute when shared data changes
    return sharedData;
  }, [sharedData, sharedDataChangeCount]); // 🚀 ZONE-BASED: Only shared data changes!
};

/**
 * 🚀 ZONE-BASED: Stable wrapper for useCharacterZone - only updates on Character Zone changes
 * This prevents unnecessary re-renders by creating stable references
 */
export const useStableCharacterZone = () => {
  // 🚀 ZONE-BASED: Watch Character Zone changes
  const characterZoneChangeCount = useSelector(selectCharacterZoneChangeCount);

  // Import the hook dynamically to avoid circular dependencies
  const { useCharacterZone } = require('../hooks/core/useZoneHooks');
  const characterZone = useCharacterZone();

  return useMemo(() => {
    if (process.env.NODE_ENV === 'development' && window.DEBUG_MEMOIZATION) {
      console.log('[ZONE_STABLE_HOOK] useStableCharacterZone updated, changeCount:', characterZoneChangeCount);
    }

    // Return the character zone data - this will only re-compute when Character Zone changes
    return characterZone;
  }, [characterZone, characterZoneChangeCount]); // 🚀 ZONE-BASED: Only Character Zone changes!
};
